// HRMS — Attendance + Leaves + Holidays
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import hr from '../api';
import { PageHeader, Btn, Drawer, Input, Select, Table, Pill, Avatar, fmtDate, fmtDateTime, todayISO, statusTone, SOFT, PANEL, Kpi } from '../ui';

// ─── Attendance ─────────────────────────────────────────────────────────────
export function Attendance({ companyId }) {
  const [today, setToday] = useState(null);
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selEmp, setSelEmp] = useState('');
  const reload = async () => {
    if (!companyId) return;
    setToday(await hr.get('/attendance/today', { company_id: companyId }));
    setRecords(await hr.get('/attendance', { company_id: companyId }));
    setEmployees(await hr.get('/employees', { company_id: companyId }));
  };
  useEffect(() => { reload(); }, [companyId]);

  const doCheckIn = async () => {
    if (!selEmp) return toast.error('Select employee');
    try { await hr.post('/attendance/check-in', { employee_id: selEmp }); toast.success('Checked in'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const doCheckOut = async () => {
    if (!selEmp) return toast.error('Select employee');
    try { await hr.post('/attendance/check-out', { employee_id: selEmp }); toast.success('Checked out'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="hr-attendance">
      <PageHeader title="Attendance" subtitle="Check-in/out · regularization · shift-aware" />
      {today && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <Kpi label="Total" value={today.total_employees} />
          <Kpi label="Checked In" value={today.checked_in} tone="green" />
          <Kpi label="Checked Out" value={today.checked_out} tone="blue" />
          <Kpi label="Absent" value={today.absent} tone={today.absent ? 'red' : 'neutral'} />
        </div>
      )}
      <div className={`${SOFT} rounded-xl p-4 flex gap-3 items-end mb-4`}>
        <Select label="Employee" value={selEmp} onChange={(e) => setSelEmp(e.target.value)}>
          <option value="">— Select —</option>
          {employees.map((e) => <option key={e.id} value={e.id}>{e.emp_code} · {e.full_name}</option>)}
        </Select>
        <Btn variant="accent" onClick={doCheckIn} data-testid="hr-check-in">Check In</Btn>
        <Btn variant="ghost" onClick={doCheckOut} data-testid="hr-check-out">Check Out</Btn>
      </div>
      <Table
        columns={[
          { key: 'date', label: 'Date', render: (r) => fmtDate(r.date) },
          { key: 'emp_code', label: 'Code', render: (r) => <span className="font-mono text-xs text-white/55">{r.emp_code}</span> },
          { key: 'employee_name', label: 'Employee' },
          { key: 'check_in', label: 'In', render: (r) => r.check_in ? new Date(r.check_in).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—' },
          { key: 'check_out', label: 'Out', render: (r) => r.check_out ? new Date(r.check_out).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—' },
          { key: 'hours_worked', label: 'Hours', align: 'right', render: (r) => r.hours_worked ? `${r.hours_worked}h` : '—' },
          { key: 'status', label: 'Status', render: (r) => <Pill tone={statusTone(r.status)}>{r.status}</Pill> },
        ]}
        rows={records.slice(0, 100)} empty="No records."
      />
    </div>
  );
}

// ─── Leaves ─────────────────────────────────────────────────────────────────
export function Leaves({ companyId }) {
  const [policies, setPolicies] = useState([]);
  const [requests, setRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [openP, setOpenP] = useState(false);
  const [openR, setOpenR] = useState(false);
  const [pForm, setPForm] = useState({ annual_quota: 12, code: 'CL', color: '#1AA84B' });
  const [rForm, setRForm] = useState({ days: 1 });
  const [balances, setBalances] = useState([]);

  const reload = async () => {
    if (!companyId) return;
    setPolicies(await hr.get('/leave-policies', { company_id: companyId }));
    setRequests(await hr.get('/leave-requests', { company_id: companyId }));
    setEmployees(await hr.get('/employees', { company_id: companyId }));
  };
  useEffect(() => { reload(); }, [companyId]);

  const savePolicy = async () => {
    try {
      if (!pForm.name) return toast.error('Name required');
      await hr.post('/leave-policies', { ...pForm, company_id: companyId, annual_quota: Number(pForm.annual_quota) });
      toast.success('Saved'); setOpenP(false); setPForm({ annual_quota: 12, code: 'CL', color: '#1AA84B' }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const loadBalances = async (eid) => {
    if (!eid) { setBalances([]); return; }
    setBalances(await hr.get('/leave-balances', { employee_id: eid }));
  };

  const saveReq = async () => {
    try {
      if (!rForm.employee_id || !rForm.policy_id || !rForm.from_date || !rForm.to_date) return toast.error('All fields required');
      await hr.post('/leave-requests', { ...rForm, days: Number(rForm.days) });
      toast.success('Leave requested'); setOpenR(false); setRForm({ days: 1 }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const decide = async (id, action) => {
    try { await hr.post(`/leave-requests/${id}/decide`, { action }); toast.success(action === 'approve' ? 'Approved' : 'Rejected'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="hr-leaves">
      <PageHeader title="Leaves" subtitle="Policies · balances · approval workflow"
        right={<div className="flex gap-2"><Btn variant="ghost" onClick={() => setOpenP(true)}>+ Policy</Btn><Btn variant="accent" onClick={() => setOpenR(true)} data-testid="hr-new-leave">+ Apply Leave</Btn></div>} />

      <div className={`${SOFT} rounded-xl p-4 mb-4`}>
        <div className="text-[10px] tracking-[0.2em] uppercase text-white/45 mb-2">Leave Policies</div>
        <div className="flex flex-wrap gap-2">
          {!policies.length && <span className="text-white/40 text-sm italic">No policies yet — add Casual, Sick, Earned leave.</span>}
          {policies.map((p) => (
            <div key={p.id} className="flex items-center gap-2 px-3 py-1.5 rounded border border-white/10 bg-white/[0.03]">
              <span style={{ backgroundColor: p.color }} className="w-2 h-2 rounded-full" />
              <span className="text-sm text-white">{p.name}</span>
              <span className="text-xs text-white/55">{p.annual_quota}d/year</span>
            </div>
          ))}
        </div>
      </div>

      <Table
        columns={[
          { key: 'req_no', label: '#', render: (r) => <span className="font-mono text-xs text-white/70">{r.req_no}</span> },
          { key: 'employee_name', label: 'Employee' },
          { key: 'policy_name', label: 'Policy' },
          { key: 'from_date', label: 'From', render: (r) => fmtDate(r.from_date) },
          { key: 'to_date', label: 'To', render: (r) => fmtDate(r.to_date) },
          { key: 'days', label: 'Days', align: 'right' },
          { key: 'status', label: 'Status', render: (r) => <Pill tone={statusTone(r.status)}>{r.status}</Pill> },
          { key: 'a', label: '', render: (r) => r.status === 'pending' ? (<div className="flex gap-2 justify-end"><button className="text-xs text-emerald-300 hover:text-emerald-200" onClick={() => decide(r.id, 'approve')} data-testid={`leave-approve-${r.req_no}`}>Approve</button><button className="text-xs text-rose-400 hover:text-rose-300" onClick={() => decide(r.id, 'reject')}>Reject</button></div>) : null },
        ]}
        rows={requests} empty="No leave requests."
      />

      <Drawer open={openP} onClose={() => setOpenP(false)} title="New Leave Policy" width="max-w-lg">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Name *" value={pForm.name || ''} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} />
          <Input label="Code *" value={pForm.code || ''} onChange={(e) => setPForm({ ...pForm, code: e.target.value })} />
          <Input label="Annual Quota (days)" type="number" value={pForm.annual_quota} onChange={(e) => setPForm({ ...pForm, annual_quota: e.target.value })} />
          <Input label="Color" type="color" value={pForm.color} onChange={(e) => setPForm({ ...pForm, color: e.target.value })} />
        </div>
        <div className="flex justify-end gap-2 mt-5"><Btn variant="ghost" onClick={() => setOpenP(false)}>Cancel</Btn><Btn variant="accent" onClick={savePolicy}>Save</Btn></div>
      </Drawer>

      <Drawer open={openR} onClose={() => setOpenR(false)} title="Apply Leave" width="max-w-xl">
        <div className="grid grid-cols-2 gap-3">
          <Select label="Employee *" value={rForm.employee_id || ''} onChange={(e) => { setRForm({ ...rForm, employee_id: e.target.value }); loadBalances(e.target.value); }} data-testid="leave-emp">
            <option value="">— Select —</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.emp_code} · {e.full_name}</option>)}
          </Select>
          <Select label="Policy *" value={rForm.policy_id || ''} onChange={(e) => setRForm({ ...rForm, policy_id: e.target.value })} data-testid="leave-policy">
            <option value="">— Select —</option>
            {policies.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <Input label="From *" type="date" value={rForm.from_date || ''} onChange={(e) => setRForm({ ...rForm, from_date: e.target.value })} />
          <Input label="To *" type="date" value={rForm.to_date || ''} onChange={(e) => setRForm({ ...rForm, to_date: e.target.value })} />
          <Input label="Days" type="number" value={rForm.days} onChange={(e) => setRForm({ ...rForm, days: e.target.value })} />
          <Input label="Reason" value={rForm.reason || ''} onChange={(e) => setRForm({ ...rForm, reason: e.target.value })} />
        </div>
        {balances.length > 0 && (
          <div className={`${SOFT} rounded-lg p-3 mt-3`}>
            <div className="text-[10px] tracking-[0.2em] uppercase text-white/45 mb-2">Balances</div>
            {balances.map((b) => (
              <div key={b.id} className="flex justify-between text-sm py-1">
                <span className="text-white/80">{b.policy_name}</span>
                <span className="tabular-nums text-emerald-300">{b.available} days</span>
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-end gap-2 mt-5"><Btn variant="ghost" onClick={() => setOpenR(false)}>Cancel</Btn><Btn variant="accent" onClick={saveReq} data-testid="leave-save">Submit</Btn></div>
      </Drawer>
    </div>
  );
}

// ─── Holidays ───────────────────────────────────────────────────────────────
export function Holidays({ companyId }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: 'public' });
  const reload = async () => { if (companyId) setRows(await hr.get('/holidays', { company_id: companyId })); };
  useEffect(() => { reload(); }, [companyId]);

  const save = async () => {
    try {
      if (!form.date || !form.name) return toast.error('Date and name required');
      await hr.post('/holidays', { ...form, company_id: companyId });
      toast.success('Saved'); setOpen(false); setForm({ type: 'public' }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  return (
    <div data-testid="hr-holidays">
      <PageHeader title="Holiday Calendar" subtitle={`${rows.length} holidays`}
        right={<Btn variant="accent" onClick={() => setOpen(true)} data-testid="hr-new-holiday">+ Add Holiday</Btn>} />
      <Table
        columns={[
          { key: 'date', label: 'Date', render: (r) => <span className="font-medium text-white">{fmtDate(r.date)}</span> },
          { key: 'name', label: 'Name' },
          { key: 'type', label: 'Type', render: (r) => <Pill tone={r.type === 'public' ? 'green' : 'blue'}>{r.type}</Pill> },
          { key: 'a', label: '', render: (r) => <button className="text-xs text-rose-400/70 hover:text-rose-400" onClick={async () => { await hr.del(`/holidays/${r.id}`); reload(); }}>Delete</button> },
        ]}
        rows={rows} empty="No holidays added yet."
      />
      <Drawer open={open} onClose={() => setOpen(false)} title="New Holiday" width="max-w-lg">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Date *" type="date" value={form.date || ''} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          <Input label="Name *" value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Select label="Type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
            <option value="public">Public</option><option value="optional">Optional</option><option value="restricted">Restricted</option>
          </Select>
        </div>
        <div className="flex justify-end gap-2 mt-5"><Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn><Btn variant="accent" onClick={save}>Save</Btn></div>
      </Drawer>
    </div>
  );
}
